import { useEffect, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, Input, message } from 'antd';
import { DangerousActionModal, FormField } from '../../shared/form';
import { PlanDraftDeleteSchema } from '../../schemas/subscription';
import type { PlanDraftDeleteDTO } from '../../schemas/subscription';
import { PlanDraftDeletedSchema, PlansPageSchema, planDraftStillListed } from './contracts';
import type { Plan } from './contracts';
import { planDraftReadbackPath, subscriptionRequest, writeOptions } from './api';
import { ReadError } from './common';

export function PlanDraftDelete({ brand, record, canWrite, onClose, onReload }: { brand: string; record: Plan; canWrite: boolean; onClose: () => void; onReload: () => void }) {
  const form = useForm<PlanDraftDeleteDTO>({ resolver: zodResolver(PlanDraftDeleteSchema), mode: 'onBlur', defaultValues: { reason: '' } });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const alive = useRef(true);
  const key = useRef(crypto.randomUUID());
  useEffect(() => () => { alive.current = false; }, []);
  const stillListed = () => planDraftStillListed(brand, record.id, cursor => subscriptionRequest(planDraftReadbackPath(brand, record.productId, cursor), PlansPageSchema));
  const submit = async (values: PlanDraftDeleteDTO) => {
    if (inFlight.current || error || !canWrite || record.status !== 'DRAFT' || record.brand !== brand) return;
    inFlight.current = true; setSaving(true);
    try {
      const receipt = await subscriptionRequest(`/api/admin/subscriptions/plans/${record.id}/delete-draft`, PlanDraftDeletedSchema, writeOptions(brand, { expectedRevision: record.revision, idempotencyKey: key.current, reason: values.reason }));
      if (receipt.id !== record.id || receipt.productId !== record.productId || receipt.planVersion !== record.planVersion || receipt.revision !== record.revision + 1) throw new Error('刪除回覆與目標版本不符');
      if (await stillListed()) throw new Error('草稿仍出現在最新列表，刪除結果未確認');
      if (alive.current) { message.success('草稿已刪除並重新讀取列表'); onReload(); onClose(); }
    } catch (cause) {
      // The server may have committed before the response failed. Read first and
      // never reissue a mutation from this stale confirmation dialog.
      let readback = '最新列表已重新讀取；請關閉視窗後依最新狀態確認。';
      try {
        if (!(await stillListed())) {
          if (alive.current) { message.success('草稿已不在最新列表，刪除結果已確認'); onReload(); onClose(); }
          return;
        }
      } catch { readback = '最新列表仍無法讀取，請稍後重新整理確認，勿重複提交。'; }
      if (alive.current) { setError(`${cause instanceof Error ? cause.message : '刪除結果未確認'}。${readback}`); onReload(); }
    } finally {
      if (alive.current) { inFlight.current = false; setSaving(false); }
    }
  };
  return <DangerousActionModal open title="確認刪除草稿" warning={<>目標品牌 <strong>{brand}</strong> · {record.productId} v{record.planVersion}（ID #{record.id}）。只會移除此未發布草稿，既有版本號與歷史紀錄會保留。</>} confirmKeyword={String(record.id)} confirmHint="請輸入草稿 ID 確認刪除" confirmLabel="刪除草稿" confirming={saving} isExtraValid={canWrite && PlanDraftDeleteSchema.safeParse(form.watch()).success && !error} onConfirm={() => void form.handleSubmit(submit)()} onCancel={() => !saving && onClose()} extra={<FormProvider {...form}><Form layout="vertical"><FormField name="reason" label="刪除原因" required control={<Input.TextArea rows={3} disabled={saving || !!error} />} /><ReadError error={error} /></Form></FormProvider>} />;
}
